import React from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import assets
import { Images } from "../../../utils/Images";

// Styled components
const ProgressContainer = styled(Box)(({ theme, isMobile }) => ({
  background: "#FFFFFF",
  borderRadius: isMobile ? "12px" : "20px",
  padding: isMobile ? "12px" : "24px",
  boxShadow: "0px 7px 7px 0px #00000017, 0px 45px 13px 0px #00000000",
}));

const ProgressBarContainer = styled(Box)(({ theme, isMobile }) => ({
  position: "relative",
//   marginBottom: isMobile ? "24px" : "32px",
  marginTop: isMobile ? "12px" : "16px",
}));

const ProgressLabel = styled(Chip)(({ theme, isMobile }) => ({
  position: "absolute",
  top: isMobile ? "-4px" : "-8px",
  background: "#8EA946",
  color: "#FFFFFF",
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontSize: isMobile ? "12px" : "16px",
  height: isMobile ? "20px" : "28px",
  borderRadius: "6px",
  transform: "translateX(-50%)",
  zIndex: 2,
  "& .MuiChip-label": {
    padding: isMobile ? "0 8px" : "0 16px",
  },
}));

const StatsContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: isMobile ? "8px" : "16px",
  marginTop: isMobile ? "12px" : "40px",
}));

const StatItem = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: isMobile ? "6px" : "14px",
  height: isMobile ? "36px" : "48px",
  flex: 1,
  minWidth: 0,
}));

const RupeeContainer = styled(Box)(({ theme, isMobile }) => ({
  width: isMobile ? "28px" : "50px",
  height: isMobile ? "28px" : "50px",
  background: "#F5F8ED",
  borderRadius: isMobile ? "8px" : "11px",
  border: "0.37px solid #D1DFAD",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "16px" : "26px",
    height: isMobile ? "16px" : "26px",
  },
}));

const StatValue = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontStyle: "Bold",
  fontSize: isMobile ? "14px" : "16px",
  lineHeight: isMobile ? "16px" : "18.39px",
  letterSpacing: "0%",
  verticalAlign: "middle",
  textTransform: "capitalize",
  color: "#1D1D1B",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
}));

const StatLabel = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "500",
  fontStyle: "Medium",
  fontSize: isMobile ? "12px" : "16px",
  lineHeight: isMobile ? "14px" : "18.39px",
  letterSpacing: "0%",
  verticalAlign: "middle",
  textTransform: "capitalize",
  color: "#686866",
}));

const SocialIconButton = styled(IconButton)(({ theme, isMobile }) => ({
  width: isMobile ? "28px" : "40px",
  height: isMobile ? "28px" : "40px",
  border: "1px solid #E8E8E8",
  borderRadius: isMobile ? "8px" : "10px",
  padding: isMobile ? "4px" : "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f8f8f8",
    borderColor: "#d0d0d0",
  },
  "& img": {
    width: isMobile ? "16px" : "24px",
    height: isMobile ? "16px" : "24px",
  },
}));

const formatCompactAmount = (amount) => {
  const n = Number(amount) || 0;
  if (n >= 10000000) {
    const v = n / 10000000;
    const s = v % 1 === 0 ? String(v.toFixed(0)) : String(v.toFixed(1));
    return `₹${s}CR`;
  }
  if (n >= 100000) {
    const v = n / 100000;
    const s = v % 1 === 0 ? String(v.toFixed(0)) : String(v.toFixed(1));
    return `₹${s}L`;
  }
  if (n >= 1000) {
    return `₹${Math.round(n / 1000)}K`;
  }
  return `₹${n}`;
};

// Helper function to format currency
const formatCurrency = (amount, isMobile = false) => {
  if (isMobile) {
    // Life/mobile UI shows compact values without the ₹ symbol (rupee icon is separate).
    return formatCompactAmount(amount);
  }
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
};

// Helper function to calculate progress percentage
const calculateProgress = (raised, goal) => {
  return Math.min(Math.round((raised / goal) * 100), 100);
};

const CampaignProgress = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    progressTitle,
    goalAmount,
    raisedAmount,
    backers,
    hide_goal = 0,
    hide_raised = 0,
    hideAll = 0,
  } = campaignData;

  // Normalize hide_goal, hide_raised, and hideAll to numbers (handle string "1" or "0")
  const hideGoal = Number(hide_goal) || 0;
  const hideRaised = Number(hide_raised) || 0;
  const hideAllProgress = Number(hideAll) || 0;

  // If hideAll is 1, don't render the entire progress bar component
  if (hideAllProgress === 1) {
    return null;
  }

  const progressPercentage = calculateProgress(raisedAmount, goalAmount);

  const handleSocialShare = (platform) => {
    console.log(`Sharing campaign "${progressTitle}" on ${platform}`);
  };

  return (
    <ProgressContainer isMobile={isMobile}>
      {/* Title and Social Icons */}
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: isMobile ? 1 : 1,
          padding: isMobile ? "6px 6px 6px 0px" : "10px 10px 10px 0px",
        }}
      >
        {/* Progress Title */}
        {/* <Typography
          variant="h2"
          sx={{
            // fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontStyle: "Bold",
            fontSize: isMobile ? "16px" : "24px",
            lineHeight: "100%",
            letterSpacing: "-1%",
            verticalAlign: "middle",
            textTransform: "capitalize",
            color: "#1D1D1B",
            flex: 1,
            // Custom media query for screens 1300px and below
            "@media (max-width: 1300px)": {
              fontSize: isMobile ? "16px" : "20px",
              lineHeight: "100%",
            },
            "@media (max-width: 1100px)": {
                fontSize: isMobile ? "16px" : "17px",
                lineHeight: "100%",
              },
          }}
        >
          {progressTitle}
        </Typography> */}

        {/* Social Media Icons - Hidden on mobile */}
        {/* {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <SocialIconButton
              isMobile={isMobile}
              onClick={() => handleSocialShare("Facebook")}
              aria-label="Share on Facebook"
            >
              <img src={Images.facebook} alt="Facebook" />
            </SocialIconButton>
            <SocialIconButton
              isMobile={isMobile}
              onClick={() => handleSocialShare("LinkedIn")}
              aria-label="Share on LinkedIn"
            >
              <img src={Images.linkedin} alt="LinkedIn" />
            </SocialIconButton>
            <SocialIconButton
              isMobile={isMobile}
              onClick={() => handleSocialShare("Twitter")}
              aria-label="Share on Twitter"
            >
              <img src={Images.twitter} alt="Twitter" />
            </SocialIconButton>
          </Box>
        )}
      </Box> */}

      {/* Progress Bar */}
      <ProgressBarContainer isMobile={isMobile}>
        <ProgressLabel 
          label={`${progressPercentage}%`} 
          isMobile={isMobile}
          sx={{
            left: progressPercentage === 0
              ? isMobile ? '0px' : '25px' // Align at start when 0%
              : progressPercentage >= 100 ? "calc(100% - 20px)" : 
                 progressPercentage <= 5 ? "40px" : `${progressPercentage}%`,
          }}
        />
        <Box
          sx={{
            width: "100%",
            height: isMobile ? "12px" : "16px",
            backgroundColor: "#E5E5E5",
            borderRadius: isMobile ? "16px" : "21px",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: `${progressPercentage}%`,
              height: isMobile ? "6px" : "8px",
              backgroundColor: "#8EA946",
              left: isMobile ? "3px" : "4px",
              borderRadius: isMobile ? "8px" : "10px",
              transition: "width 0.3s ease",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: 0,
                width: isMobile ? "8px" : "10px",
                height: "100%",
                background: "linear-gradient(to right, #8EA946 0%, #8EA946 70%, transparent 100%)",
                borderRadius: "0 4px 4px 0",
              },
            }}
          />
        </Box>
      </ProgressBarContainer>

      {/* Statistics */}
      <StatsContainer isMobile={isMobile}>
        {/* Case 1: hide_goal = 0, hide_raised = 0: Show Goal, Raised, Donors */}
        {hideGoal === 0 && hideRaised === 0 && (
          <>
            <StatItem isMobile={isMobile} sx={{ justifyContent: "flex-start" }}>
              <RupeeContainer isMobile={isMobile}>
                <img src={Images.rupee} alt="Rupee" />
              </RupeeContainer>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "flex-start" }}>
                <StatLabel isMobile={isMobile}>Goal</StatLabel>
                <StatValue isMobile={isMobile}>{formatCurrency(goalAmount, isMobile)}</StatValue>
              </Box>
            </StatItem>

            <Divider orientation="vertical" flexItem sx={{ height: isMobile ? "36px" : "48px" }} />

            <StatItem isMobile={isMobile} sx={{ justifyContent: "center" }}>
              <RupeeContainer isMobile={isMobile}>
                <img src={Images.rupee} alt="Rupee" />
              </RupeeContainer>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "flex-start" }}>
                <StatLabel isMobile={isMobile} sx={{ fontWeight: 700 }}>Raised</StatLabel>
                <StatValue isMobile={isMobile} sx={{ fontWeight: 800 }}>{formatCurrency(raisedAmount, isMobile)}</StatValue>
              </Box>
            </StatItem>

            <Divider orientation="vertical" flexItem sx={{ height: isMobile ? "36px" : "48px" }} />
          </>
        )}

        {/* Case 2: hide_goal = 1, hide_raised = 0: Show Raised (in Goal's place), Text, Donors */}
        {hideGoal === 1 && hideRaised === 0 && (
          <>
            <StatItem isMobile={isMobile} sx={{ justifyContent: "flex-start" }}>
              <RupeeContainer isMobile={isMobile}>
                <img src={Images.rupee} alt="Rupee" />
              </RupeeContainer>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "flex-start" }}>
                <StatLabel isMobile={isMobile} sx={{ fontWeight: 700 }}>Raised</StatLabel>
                <StatValue isMobile={isMobile} sx={{ fontWeight: 800 }}>{formatCurrency(raisedAmount, isMobile)}</StatValue>
              </Box>
            </StatItem>

            <Divider orientation="vertical" flexItem sx={{ height: isMobile ? "36px" : "48px" }} />

            <StatItem isMobile={isMobile} sx={{ justifyContent: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "center", textAlign: "center" }}>
                <StatLabel isMobile={isMobile} sx={{ 
                  fontSize: isMobile ? "12px" : "16px",
                  lineHeight: isMobile ? "16px" : "20px",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  wordBreak: "break-word"
                }}>
                  {isMobile ? (
                    <>For the Profiled<br />patient and other patient in need</>
                  ) : (
                    <>For the Profiled patient and other patient in need</>
                  )}
                </StatLabel>
              </Box>
            </StatItem>

            <Divider orientation="vertical" flexItem sx={{ height: isMobile ? "36px" : "48px" }} />
          </>
        )}

        {/* Case 3: hide_goal = 1, hide_raised = 1: Show Text, Donors */}
        {hideGoal === 1 && hideRaised === 1 && (
          <>
            <StatItem isMobile={isMobile} sx={{ justifyContent: "flex-start", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "flex-start", textAlign: "left" }}>
                <StatLabel isMobile={isMobile} sx={{ 
                  fontSize: isMobile ? "12px" : "16px",
                  lineHeight: isMobile ? "16px" : "20px",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  wordBreak: "break-word"
                }}>
                  {isMobile ? (
                    <>For the Profiled<br />patient and other patient in need</>
                  ) : (
                    <>For the Profiled patient and other patient in need</>
                  )}
                </StatLabel>
              </Box>
            </StatItem>

            <Divider orientation="vertical" flexItem sx={{ height: isMobile ? "36px" : "48px" }} />
          </>
        )}

        {/* Donors - always shown */}
        <StatItem isMobile={isMobile} sx={{ justifyContent: "flex-end" }}>
          <RupeeContainer isMobile={isMobile}>
            <img src={Images.rupee} alt="Rupee" />
          </RupeeContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1px" : "2px", alignItems: "flex-start" }}>
            <StatLabel isMobile={isMobile}>Donors</StatLabel>
            <StatValue isMobile={isMobile}>{backers.toLocaleString()}</StatValue>
          </Box>
        </StatItem>
      </StatsContainer>
    </ProgressContainer>
  );
};

export default CampaignProgress;
